import {
	Entities,
	EntityStatus,
	ProductsApi,
	ProvisionVat,
	Sensor,
	Transceiver,
	Vat,
	VatFormula,
	VatTemperatureOffset,
	VatsApi,
} from '@/api';
import axios, { AxiosError } from 'axios';
import { calculateVatHeight } from '../models/vat-heights';
import { ApiUtils } from '@/common/ApiUtils';

export class VatService {
	constructor(
		private vatsApi: VatsApi = new VatsApi(),
		private productsApi: ProductsApi = new ProductsApi(),
	) {}

	public static getVatHeight(vatFormula: string, vatSize: string): number | undefined {
		return calculateVatHeight(vatFormula, vatSize.toString());
	}

	public async getVatById(
		vatId: number,
		options: any,
	): Promise<{ vat?: Vat; errorMessage?: string }> {
		try {
			const response = await this.vatsApi.getVatById(vatId, options);
			return { vat: response.data as Vat }; // In data is wrong - Worried it will break other areas to change it
		} catch (e) {
			return ApiUtils.handleApiError(e, `Failed to fetch Product by id: ${vatId}`);
		}
	}

	public async cancelVat(
		vat: Vat,
		actionReason?: string | null,
	): Promise<{ errorMessage: string } | void> {
		let errorMessage: string | undefined;
		await this.vatsApi
			.cancelVat(vat.id, {
				actionReason: actionReason ?? undefined,
			})
			.catch(e => {
				const error: AxiosError = e as AxiosError;
				errorMessage = error.response?.data.message ?? 'Failed to cancel vat';
			});
		if (errorMessage) {
			return { errorMessage };
		}
	}

	public async removeVat(
		vat: Vat,
		actionReason?: string | null,
	): Promise<{ errorMessage: string } | void> {
		let errorMessage: string | undefined;
		await this.vatsApi
			.deactivateVat(vat.id, {
				actionReason: actionReason ?? undefined,
			})
			.catch(e => {
				const error: AxiosError = e as AxiosError;
				errorMessage = error.response?.data.message ?? 'Failed to remove vat';
			});
		if (errorMessage) {
			return { errorMessage };
		}
	}

	public async install(
		vat: Vat & { transceiver: Transceiver; sensors: Sensor[] },
	): Promise<{ errorMessage: string } | void> {
		let errorMessage: string | undefined;
		await this.vatsApi
			.installVat(vat.id, {
				data: {
					transceiverId: vat.transceiver.id,
					entityType: Entities.Vats,
					sensors: vat.sensors,
				},
			})
			.catch(e => {
				const error = e as AxiosError;
				errorMessage = error.response?.data.message ?? 'Failed to install vat';
			});
		if (errorMessage) {
			return { errorMessage };
		}
	}

	public async provision(vat: Vat): Promise<{ errorMessage: string } | void> {
		if (this.hasRequiredVatAttributes(vat)) {
			let errorMessage: string | undefined;
			await this.vatsApi.provisionVat(vat.id, { data: vat }).catch(e => {
				const error: AxiosError = e as AxiosError;
				errorMessage = error.response?.data.errors ?? 'Failed to provision vat';
			});
			if (errorMessage) {
				return { errorMessage };
			}
		} else {
			return { errorMessage: 'Vat is missing required attributes' };
		}
	}

	public async signOffVat(vat: Vat): Promise<{ errorMessage: string } | void> {
		let errorMessage: string | undefined;
		await this.vatsApi.signoffVat(vat.id).catch(e => {
			const error = e as AxiosError;
			errorMessage = error.response?.data.message ?? 'Failed to sign vat off';
		});
		if (errorMessage) {
			return { errorMessage };
		}
	}

	public async moveVatToSignOff(vat: Vat): Promise<{ errorMessage: string } | void> {
		let errorMessage: string | undefined;
		if (!vat.productId) {
			return { errorMessage: 'This vat has no associated product' };
		}
		await this.productsApi
			.changeProductStatus(vat.productId, Entities.Vats, vat.id, {
				newStatus: EntityStatus.SignOff,
			})
			.catch(e => {
				const error = e as AxiosError;
				errorMessage = error.response?.data.message ?? 'Failed to move vat to sign off';
			});
		if (errorMessage) {
			return { errorMessage };
		}
	}

	public async convertDistanceToVolume(vat: Vat): Promise<number | null> {
		try {
			let convertDistanceUrl =
				process.env.VUE_APP_API_URL.replace(/\/+$/, '') + '/vats/convert/convertDistance';
			convertDistanceUrl += `?distance=${vat.emptyDistance}
        &model=${vat.sizeString}
        &offset=${vat.vatOffset}
        &formula=${vat.formula}
        &height=${vat.height}`;
			const emptyVolumeResponse = await axios.get(convertDistanceUrl);
			const { emptyVolume } = emptyVolumeResponse.data;
			return Math.round(emptyVolume);
		} catch (e) {
			return null;
		}
	}

	public async closeMilkScore(
		id: number,
		options?: any,
	): Promise<{ errorMessage?: string }> {
		try {
			await this.vatsApi.vatsVatIdCloseMilkScoreGet(id);
			return {};
		} catch (e) {
			return ApiUtils.handleApiError(e, 'Failed to Close Milk Score');
		}
	}

	public async updateVatState(vat: Vat): Promise<void> {
		const { id, state } = vat;
		await this.vatsApi.updateVat(vat.id, { data: { id, state } });
	}

	public async updateVatOffsetTemperature(
		vatId: number,
		vatOffsetTemp: number,
	): Promise<{ errorMessage?: string } | void> {
		try {
			await this.vatsApi.setTemperatureOffset(vatId, { temperatureOffset: vatOffsetTemp });
		} catch (e) {
			return ApiUtils.handleApiError(e, 'There was a problem updating the vat offset temperature');
		}
	}

	public async updateEmptyDistance(
		vatId: number,
		emptyDistance: number,
	): Promise<{ errorMessage?: string } | void> {
		try {
			await this.vatsApi.setEmptyDistance(vatId, { data: { emptyDistance } });
		} catch (e) {
			return ApiUtils.handleApiError(e, 'There was a problem updating the empty distance');
		}
	}

	private hasRequiredVatAttributes(vat: Partial<Vat>): vat is ProvisionVat {
		if (
			!vat.state ||
			!vat.formula ||
			!vat.sizeString ||
			!(typeof vat.vatOffset === 'number') ||
			!(typeof vat.emptyDistance === 'number') ||
			!(typeof vat.stirrerThresh === 'number' || vat.stirrerThresh === null)
		) {
			return false;
		}
		if (vat.formula === VatFormula.Custom) {
			if (!vat.formulaParameters || !vat.formulaId) {
				return false;
			}
			const hasFormulaParams = Object.entries(vat.formulaParameters).every(
				parameter => parameter !== null,
			);
			return hasFormulaParams;
		}
		return true;
	}
}
